import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  ProgressBar,
  Form,
  Modal,
} from "react-bootstrap";
import { LuDownload, LuUploadCloud } from "react-icons/lu";
import { IoSend } from "react-icons/io5";
import { BsFileEarmarkText, BsFileEarmarkPlus, BsFileEarmarkCheck } from "react-icons/bs";
import {
  FiClipboard,
  FiDollarSign,
  FiCreditCard,
  FiUsers,
  FiTrendingUp,
  FiTrendingDown,
  FiGrid,
  FiTrash2,
  FiX
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiConfig from "../../../credentials/apiConfig";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "react-toastify";
import Loader from "../../../../components/Loader";
import logoIcon from "../../../../images/fav-icon.png";
import "./styles.scss";

// Default categories (used when no PAR data loaded yet)
const DEFAULT_CATEGORIES = [
  { key: "Planning", label: "Planning" },
  { key: "Assets", label: "Assets" },
  { key: "Liabilities", label: "Liabilities" },
  { key: "Equity", label: "Equity" },
  { key: "Revenue", label: "Revenue" },
  { key: "Operating Expenses", label: "Operating Expenses" },
  { key: "Other Income and Expenses", label: "Other Income and Expenses" },
  { key: "General", label: "General" },
];

const MAX_PARALLEL_S3_UPLOADS = 3;
const MAX_S3_UPLOAD_RETRIES = 2;

const SUGGESTED_QUESTIONS = {
  Planning: [
    "What are the key risks?",
    "Stale reconciling items?",
    "AR allowance adequate?",
    "Investment impairment risk?",
  ],
  Assets: [
    "What are the key risks?",
    "Asset impairment concerns?",
    "Depreciation method appropriate?",
    "Any unrecorded assets?",
  ],
  Liabilities: [
    "What are the key risks?",
    "Unrecorded liabilities?",
    "Debt covenant compliance?",
    "Contingent liabilities?",
  ],
  Equity: [
    "What are the key risks?",
    "Stock compensation issues?",
    "Retained earnings reconciled?",
    "Treasury stock transactions?",
  ],
  Revenue: [
    "What are the key risks?",
    "Revenue recognition issues?",
    "Deferred revenue balance?",
    "Unusual revenue trends?",
  ],
  "Operating Expenses": [
    "What are the key risks?",
    "Unusual expense items?",
    "Related party transactions?",
    "Expense cutoff issues?",
  ],
};

const CATEGORY_GUIDES = {
  Assets: {
    title: "Assets Overview",
    description: "Assets represent resources owned or controlled by the entity that are expected to provide future economic benefits. In an audit engagement, this is one of the most material sections — verifying that reported assets truly exist, are properly valued, and are completely recorded is fundamental to forming an audit opinion. Misstatements in asset balances can arise from overvaluation, fictitious assets, impaired assets not written down, or improper capitalization of expenses.",
    keyAreas: [
      "**Cash and cash equivalents** — Obtain year-end bank statements and bank confirmations for all accounts. Perform bank reconciliations to identify outstanding checks, deposits in transit, and unusual reconciling items. Verify proper cutoff by tracing transactions around year-end. Assess whether restricted cash is properly disclosed and whether foreign currency balances are translated at the correct closing rate",
      "**Investments** — Confirm fair value measurements against independent pricing sources (Level 1, 2, 3 hierarchy). Review for other-than-temporary impairment (OTTI) and assess management's intent and ability to hold. Verify investment income (dividends, interest, gains/losses) against brokerage statements. For equity method investments, verify the investor's share of investee income and reconcile to investee financial statements",
      "**Receivables** — Analyze the aged receivables report to identify concentrations and overdue balances. Evaluate the adequacy of the allowance for doubtful accounts by reviewing historical write-off rates, economic conditions, and customer creditworthiness. Send accounts receivable confirmations for significant balances. Perform cutoff testing to ensure sales near year-end are recorded in the correct period. Test for fictitious receivables by vouching to shipping documents and customer purchase orders",
      "**Property, Plant & Equipment** — Perform physical inspection or verification of existence for significant additions. Review capitalization policies (threshold amounts, useful lives) for consistency with prior years and industry standards. Recalculate depreciation expense and accumulated depreciation. Assess impairment indicators per ASC 360 — evaluate carrying amount vs. undiscounted future cash flows. Verify that disposals are properly recorded and gains/losses correctly calculated",
      "**Inventory** — Observe physical inventory counts and perform test counts. Evaluate the costing methodology (FIFO, LIFO, weighted average) and verify consistent application. Test the net realizable value (NRV) for slow-moving or obsolete items. Perform inventory cutoff testing at year-end for goods in transit. For manufacturing entities, verify the allocation of overhead to work-in-progress and finished goods",
      "**Intangible Assets & Goodwill** — Verify the basis for recognition of internally developed vs. acquired intangibles. Review amortization schedules and remaining useful lives. For indefinite-lived intangibles and goodwill, evaluate management's annual impairment testing per ASC 350 — review the reasonableness of cash flow projections, discount rates, and comparable market multiples used in the valuation",
      "**Prepaid Expenses & Other Assets** — Verify that prepaid balances represent future benefits by reviewing underlying contracts. Test amortization schedules for proper expense recognition. Investigate unusual or large other asset balances for proper classification and recoverability",
    ],
    commonRisks: [
      "Overstatement of asset values through improper capitalization or failure to record impairments",
      "Fictitious or non-existent assets recorded to inflate the balance sheet",
      "Improper revenue recognition inflating receivable balances",
      "Obsolete inventory not written down to net realizable value",
      "Related-party transactions involving assets at non-arm's-length prices",
      "Incorrect foreign currency translation of overseas asset balances",
    ],
    typicalDocuments: [
      "Bank statements & reconciliations",
      "Investment portfolio statements & brokerage confirmations",
      "Aged accounts receivable report",
      "Fixed asset register & depreciation schedules",
      "Inventory count sheets & valuation reports",
      "Goodwill impairment test workpapers",
      "Prepaid expense schedules",
    ],
    whatToDo: "Upload relevant documents for each sub-category — bank statements, investment schedules, aging reports, fixed asset registers, inventory valuation reports. AuditPro AI will cross-reference your trial balance data with these documents to identify risks, anomalies, unusual trends, and areas requiring deeper substantive testing.",
  },
  Liabilities: {
    title: "Liabilities Overview",
    description: "Liabilities represent present obligations of the entity arising from past transactions or events, the settlement of which is expected to result in an outflow of economic resources. The primary audit assertion for liabilities is completeness — ensuring that all obligations are properly recorded. Understatement of liabilities is a common area of management bias, as it directly improves reported equity and financial ratios. The auditor must be vigilant about unrecorded liabilities, off-balance-sheet arrangements, and contingent obligations.",
    keyAreas: [
      "**Accounts Payable** — Perform a search for unrecorded liabilities by examining payments made after year-end, open purchase orders, and receiving reports not matched to invoices. Reconcile vendor statements to recorded payable balances. Send AP confirmations for significant vendors. Perform cutoff testing to ensure goods received before year-end have corresponding liabilities recorded. Analyze the AP aging for unusual items or credit balances that may indicate disputes or overpayments",
      "**Debt & Borrowings** — Obtain loan confirmations directly from lenders to verify principal balances, interest rates, maturity dates, and collateral. Review all debt agreements for covenant requirements and test compliance at year-end (debt-to-equity ratios, interest coverage, minimum liquidity). Verify proper classification between current and non-current portions. Recalculate interest expense and accrued interest at year-end. For new borrowings or refinancing, review terms and assess whether debt modification or extinguishment accounting applies",
      "**Accrued Expenses** — Evaluate the reasonableness of management's estimates for accrued wages, bonuses, commissions, vacation, and payroll taxes. Verify accrued interest by independent recalculation. Review accruals for legal fees, professional services, and utilities by comparing to subsequent invoices received. Assess warranty accruals based on historical claim rates and product return data. Test restructuring accruals for compliance with ASC 420 criteria",
      "**Lease Liabilities (ASC 842 / IFRS 16)** — Verify the completeness of the lease population by reviewing rent expense accounts and inquiring about embedded leases. Recalculate lease liabilities using the present value of remaining lease payments and the appropriate discount rate (incremental borrowing rate or implicit rate). Verify the right-of-use asset balance and amortization. Review lease modifications, renewals, and terminations for proper accounting treatment. Assess the adequacy of lease disclosures including maturity analysis",
      "**Contingent Liabilities & Provisions** — Review pending litigation with legal counsel (obtain attorney letter confirmations). Assess the probability and estimability of outcomes per ASC 450 — determine whether to accrue, disclose, or neither. Review environmental liabilities, product liability claims, and guarantee obligations. Inquire about regulatory investigations, tax audits, or government enforcement actions. Evaluate management's estimate for self-insurance reserves against actuarial reports",
      "**Deferred Revenue** — Verify that amounts represent genuine performance obligations not yet satisfied. Test the revenue release schedule against contract milestones, delivery confirmations, or service completion. Analyze the rollforward of deferred revenue (opening balance + additions - revenue recognized = closing balance). For subscription-based businesses, verify the methodology for recognizing revenue over the subscription period",
      "**Income Tax Liabilities** — Verify the current tax provision by reviewing the tax return or tax projection workpapers. Assess deferred tax liabilities arising from temporary differences (depreciation, revenue recognition). Review uncertain tax positions per ASC 740 using the more-likely-than-not threshold. Verify tax rate reconciliation from statutory to effective rate. Evaluate the adequacy of tax disclosures",
    ],
    commonRisks: [
      "Unrecorded liabilities intentionally omitted to improve financial ratios",
      "Underestimated accruals for litigation, warranties, or restructuring",
      "Debt covenant violations not disclosed or improperly classified",
      "Operating leases incorrectly excluded from balance sheet (ASC 842 non-compliance)",
      "Off-balance-sheet arrangements or variable interest entities not consolidated",
      "Contingent liabilities with probable losses not accrued",
    ],
    typicalDocuments: [
      "Vendor statements & AP aging reports",
      "Bank loan confirmations & debt agreements",
      "Accrual schedules & supporting calculations",
      "Lease agreements & ASC 842 calculations",
      "Attorney letters (legal representation letters)",
      "Tax provision workpapers & tax returns",
      "Deferred revenue rollforward schedules",
    ],
    whatToDo: "Upload debt agreements, vendor confirmations, accrual schedules, lease contracts, attorney letters, and tax provision workpapers. AuditPro AI will analyze for unrecorded liabilities, covenant compliance issues, and estimate reasonableness.",
  },
  Equity: {
    title: "Equity Overview",
    description: "Equity (stockholders' equity / shareholders' funds) represents the residual interest in the entity's assets after deducting all liabilities. While equity sections typically have fewer transactions than other areas, the transactions that do occur — stock issuances, buybacks, dividends, and equity-based compensation — are often highly material and complex. Audit procedures focus on verifying that all equity transactions are properly authorized, correctly valued, and appropriately disclosed. This area also requires attention to legal compliance, corporate governance, and regulatory requirements.",
    keyAreas: [
      "**Common & Preferred Stock** — Verify authorized, issued, and outstanding share counts against the articles of incorporation and board minutes. Confirm new issuances are properly valued (par value, additional paid-in capital). Review stock subscription agreements and any conversion features on preferred stock. Reconcile share counts to the transfer agent's records. Verify proper disclosure of rights, preferences, and restrictions of each share class",
      "**Retained Earnings** — Perform a rollforward from opening to closing balance (beginning balance + net income - dividends +/- prior period adjustments = ending balance). Verify that dividends declared are properly authorized by the board and comply with legal restrictions. Review for any restatements, error corrections, or changes in accounting policy affecting retained earnings. Assess accumulated deficit situations and implications for going concern",
      "**Stock-Based Compensation (ASC 718)** — Review all equity award plans (stock options, RSUs, performance shares, ESPP). Verify grant-date fair value calculations — for options, review the Black-Scholes or binomial model inputs (stock price, exercise price, expected volatility, risk-free rate, expected term, dividend yield). Test the vesting schedule and expense recognition pattern (straight-line vs. graded). Review modifications and cancellations for incremental fair value. Verify forfeiture estimates and actual forfeitures. Assess the dilutive impact on earnings per share",
      "**Treasury Stock** — Verify share repurchase transactions against board authorizations and broker confirmations. Confirm the accounting method used (cost method vs. par value method) and consistent application. Review for any reissuances of treasury shares and proper accounting. Assess compliance with share repurchase program terms and regulatory requirements (e.g., SEC Rule 10b-18 safe harbor provisions)",
      "**Other Comprehensive Income (OCI)** — Verify the components of accumulated OCI including foreign currency translation adjustments, unrealized gains/losses on available-for-sale securities, cash flow hedge adjustments, and pension/OPEB actuarial gains/losses. Test reclassification adjustments from OCI to net income. Ensure proper presentation in the statement of comprehensive income and equity rollforward",
      "**Non-Controlling Interests** — For consolidated entities, verify the calculation of NCI's share of subsidiary net income and equity. Review business combinations for proper allocation of fair value to NCI. Assess changes in ownership interests that do not result in loss of control (equity transactions) vs. those that do (deconsolidation)",
    ],
    commonRisks: [
      "Unauthorized stock issuances or treasury stock transactions",
      "Errors in stock compensation expense due to incorrect valuation model inputs",
      "Dividends declared in excess of legal limits or retained earnings",
      "Misclassification between equity and liability instruments (e.g., mandatorily redeemable shares)",
      "Failure to properly account for complex equity instruments (convertible debt, warrants)",
      "Inadequate disclosure of related-party equity transactions",
    ],
    typicalDocuments: [
      "Articles of incorporation & bylaws",
      "Board meeting minutes & resolutions",
      "Share register / transfer agent records",
      "Stock compensation plan documents & grant letters",
      "Dividend declaration records",
      "Treasury stock authorization & broker statements",
      "Equity rollforward schedules",
    ],
    whatToDo: "Upload board resolutions, share registers, stock compensation plans, dividend declarations, and equity rollforward schedules. AuditPro AI will verify calculations, check authorization, and ensure disclosure completeness.",
  },
  Revenue: {
    title: "Revenue Overview",
    description: "Revenue is presumed to be a significant risk in every audit engagement (ISA 240 / AS 2401). Because of the direct impact on profitability, financial ratios, and stock price, revenue is the area most susceptible to management manipulation. Audit procedures must address all five assertions: occurrence (revenue transactions actually happened), accuracy (correct amounts), completeness (all revenue recorded), cutoff (correct period), and classification (proper account mapping). The adoption of ASC 606 / IFRS 15 introduced the five-step revenue recognition model, adding complexity to audit procedures.",
    keyAreas: [
      "**Revenue Recognition (ASC 606 Five-Step Model)** — Step 1: Identify contracts with customers (written, oral, implied). Step 2: Identify distinct performance obligations within each contract. Step 3: Determine the transaction price including variable consideration (discounts, rebates, returns, penalties), significant financing components, and non-cash consideration. Step 4: Allocate the transaction price using standalone selling prices (adjusted market assessment, expected cost plus margin, or residual approach). Step 5: Recognize revenue when (or as) performance obligations are satisfied (point-in-time vs. over-time recognition). Test a sample of revenue transactions through all five steps",
      "**Cutoff Testing** — Select transactions around year-end (both before and after) and verify they are recorded in the correct period based on transfer of control (not just shipment or invoicing). For services, verify the stage of completion at year-end. Review credit memos and sales returns in the subsequent period for indications of premature revenue recognition. Test bill-and-hold arrangements for compliance with ASC 606 criteria",
      "**Deferred Revenue & Contract Liabilities** — Perform a rollforward analysis of deferred revenue (opening + new deferrals - revenue recognized = closing). Verify that the revenue release methodology aligns with the satisfaction of performance obligations. For multi-element arrangements, verify the allocation of transaction price to each deliverable. Review contract modifications for proper treatment (cumulative catch-up vs. prospective)",
      "**Contract Assets & Costs** — Verify contract asset balances representing work performed but not yet billed. Review incremental costs of obtaining contracts (e.g., sales commissions) capitalized under ASC 340-40 and test the amortization period. Assess contract costs for impairment when expected costs exceed expected revenue",
      "**Disaggregation & Trend Analysis** — Analyze revenue by product line, geography, customer segment, and timing of recognition to identify unusual patterns. Compare monthly revenue trends to prior year and investigate significant variances. Calculate key ratios (gross margin by product, days sales outstanding, revenue per employee) and compare to industry benchmarks. Review the top 10 customers for concentration risk and related-party relationships",
      "**Special Revenue Arrangements** — Review licensing arrangements for point-in-time vs. over-time recognition. Assess principal vs. agent considerations for gross vs. net reporting. Evaluate consignment arrangements, bill-and-hold transactions, and channel stuffing indicators. Review percentage-of-completion contracts for estimate reasonableness (for entities using ASC 606 over-time recognition based on input/output methods)",
    ],
    commonRisks: [
      "Premature revenue recognition before transfer of control",
      "Channel stuffing — shipping excess goods to distributors near period end",
      "Side agreements or verbal commitments modifying contract terms",
      "Bill-and-hold arrangements not meeting the recognition criteria",
      "Improper gross vs. net reporting overstating revenue",
      "Round-tripping transactions with related parties to inflate revenue",
      "Manipulation of percentage-of-completion estimates on long-term contracts",
    ],
    typicalDocuments: [
      "Revenue disaggregation schedules",
      "Significant customer contracts & amendments",
      "Deferred revenue rollforward",
      "Sales journal & invoice listing",
      "Shipping/delivery documentation",
      "Credit memo & returns analysis",
      "Commission expense & contract cost schedules",
    ],
    whatToDo: "Upload revenue schedules, significant contracts, deferred revenue rollforwards, sales reports, and shipping documentation. AuditPro AI will perform trend analysis, test cutoff assertions, and flag unusual revenue patterns or potential ASC 606 non-compliance.",
  },
  "Operating Expenses": {
    title: "Operating Expenses Overview",
    description: "Operating expenses encompass all costs incurred in the ordinary course of business operations, excluding cost of goods sold and financing costs. While individually many expense items may be immaterial, in aggregate they represent a significant portion of the income statement. Audit risk in this area centers on understatement (to inflate profits), improper capitalization (shifting expenses to the balance sheet), misclassification, and related-party transactions. Period-end expense manipulation through premature or delayed recognition is also a concern.",
    keyAreas: [
      "**Payroll & Employee Benefits** — Verify total compensation expense by reconciling payroll registers to the general ledger. Test a sample of employees to verify pay rates against HR records, proper tax withholdings, and benefit deductions. Review for ghost employees by comparing the payroll roster to HR headcount reports and organization charts. Verify the reasonableness of bonus accruals against performance metrics and board approvals. Review post-employment benefit obligations (pensions, OPEB) including actuarial assumptions per ASC 715",
      "**Rent & Occupancy Costs** — Reconcile rent expense to lease agreements for all locations. Under ASC 842, verify that lease expense is properly calculated (operating lease: straight-line; finance lease: amortization + interest). Review common area maintenance (CAM) charges, property taxes, and insurance reconciliations from landlords. Investigate any rent-free periods, tenant improvement allowances, or lease incentives for proper accounting treatment",
      "**Professional & Consulting Fees** — Vouch significant fees to engagement letters, invoices, and payment records. Assess whether any consulting arrangements are with related parties. Review for fees that may indicate contingent liabilities (e.g., litigation-related legal fees). Evaluate whether any consulting costs should be capitalized as part of an asset (e.g., implementation costs under ASC 350-40 for cloud computing arrangements)",
      "**Depreciation & Amortization** — Recalculate depreciation expense based on the fixed asset register and stated useful lives. Review for consistency in depreciation methods and any changes in estimates (prospective treatment per ASC 250). Verify that fully depreciated assets still in use are properly disclosed. Test amortization of intangible assets against original cost and useful life determinations",
      "**Research & Development** — Verify that R&D costs are expensed as incurred per ASC 730, unless meeting specific criteria for capitalization (e.g., software development costs under ASC 985-20 or ASC 350-40). Review for costs that may have been improperly capitalized to inflate current-period earnings. Assess R&D tax credit claims for reasonableness",
      "**Other Operating Expenses** — Review travel and entertainment expenses for compliance with company policy and reasonableness. Analyze insurance expense for completeness of coverage. Review donations and political contributions for authorization and legal compliance. Investigate any large or unusual one-time charges, restructuring costs, or write-offs. Perform variance analysis comparing current year to prior year and budget, investigating all significant deviations",
    ],
    commonRisks: [
      "Capitalizing expenses that should be recognized in the current period",
      "Understating expenses near period-end to meet earnings targets",
      "Ghost employees or unauthorized payroll transactions",
      "Related-party consulting arrangements at above-market rates",
      "Restructuring charges not meeting the criteria of ASC 420",
      "Misclassification between operating expenses and cost of goods sold",
      "Improperly deferring maintenance costs that should be expensed",
    ],
    typicalDocuments: [
      "Payroll registers & headcount reports",
      "Lease agreements & rent schedules",
      "Significant vendor invoices & engagement letters",
      "Fixed asset register & depreciation schedules",
      "R&D project cost summaries",
      "Budget-to-actual variance analysis",
      "Travel & expense policy & exception reports",
    ],
    whatToDo: "Upload expense reports, payroll summaries, vendor invoices, depreciation schedules, and budget-to-actual analyses. AuditPro AI will flag anomalies, significant variances from prior year, potential misclassifications, and related-party transaction indicators.",
  },
  General: {
    title: "General & Cross-Cutting Audit Areas",
    description: "The General section addresses audit considerations that span the entire financial statements rather than belonging to a single category. These cross-cutting areas — going concern, related parties, subsequent events, tax, and compliance — are critical to forming the overall audit opinion. Many of these areas require significant professional judgment and are frequently the subject of audit quality findings by regulators (PCAOB, AICPA). These matters often have pervasive impacts across multiple financial statement line items.",
    keyAreas: [
      "**Going Concern (ASC 205-40)** — Evaluate whether substantial doubt exists about the entity's ability to continue as a going concern for 12 months from the financial statement issuance date. Review management's plans to mitigate going concern risks (asset sales, debt refinancing, cost reductions, equity raising). Assess the feasibility and effectiveness of management's plans. Analyze liquidity metrics: current ratio, quick ratio, cash burn rate, working capital trends. Review debt maturity schedules for near-term obligations. Consider qualitative factors: loss of key customers, regulatory actions, industry downturns. Evaluate the adequacy of going concern disclosures if substantial doubt exists but is alleviated by management's plans",
      "**Related Party Transactions (ASC 850)** — Identify all related parties through board minutes, SEC filings, entity structure charts, and management inquiries. Test identified related-party transactions for arm's-length terms by comparing to market rates. Review for previously undisclosed related parties through journal entry testing, unusual transactions, and vendor/customer analysis. Assess whether related-party transactions have a valid business purpose. Verify completeness and adequacy of related-party disclosures. Review management representations regarding completeness of related-party identification",
      "**Subsequent Events (ASC 855)** — Review events from the balance sheet date to the audit report date. Distinguish between Type I (recognized) events requiring adjustment to the financial statements and Type II (non-recognized) events requiring disclosure only. Review board minutes, interim financial statements, press releases, and legal correspondence in the subsequent period. Inquire about any new commitments, borrowings, or guarantees. Review the entity's subsequent event evaluation process for completeness",
      "**Income Tax Provisions (ASC 740)** — Verify the current tax provision against tax return workpapers or projections. Evaluate deferred tax assets and liabilities arising from temporary differences (book-tax differences in depreciation, revenue recognition, reserves, etc.). Assess the realizability of deferred tax assets — evaluate the need for a valuation allowance based on positive and negative evidence (historical profitability, future projections, tax-planning strategies). Review uncertain tax positions using the two-step approach: recognition (more-likely-than-not threshold) and measurement (largest amount >50% likely). Verify the effective tax rate reconciliation from statutory to actual. Assess the impact of tax law changes enacted or substantially enacted before the balance sheet date",
      "**Accounting Estimates & Fair Value** — Evaluate management's process for developing significant estimates (allowances, impairments, fair values, useful lives). Assess the reasonableness of key assumptions using independent data sources. Review for management bias in estimates by analyzing the pattern of estimate revisions over time. For fair value measurements per ASC 820, verify the valuation hierarchy classification and the appropriateness of inputs. Review the work of management's specialists (appraisers, actuaries, valuation firms) including their competence, objectivity, and methodology",
      "**Compliance & Regulatory** — Identify applicable laws and regulations that could materially impact the financial statements. Review correspondence with regulatory agencies for any investigations, fines, or enforcement actions. Assess compliance with industry-specific regulations (banking, insurance, healthcare, government contracting). Review environmental compliance and assess the need for environmental remediation liabilities. Verify compliance with debt covenants, contractual obligations, and grant requirements",
    ],
    commonRisks: [
      "Management override of controls through journal entries or estimates",
      "Undisclosed related-party transactions designed to mislead users",
      "Inadequate going concern evaluation and disclosure",
      "Complex tax positions with insufficient documentation",
      "Subsequent events not properly identified or evaluated",
      "Management bias in accounting estimates consistently in one direction",
      "Non-compliance with laws and regulations that could be material",
    ],
    typicalDocuments: [
      "Management representation letter",
      "Attorney/legal representation letters",
      "Tax provision workpapers & tax returns",
      "Board meeting minutes (all periods)",
      "Related-party transaction schedules",
      "Cash flow forecasts & going concern analysis",
      "Regulatory correspondence & compliance reports",
      "Subsequent events checklist & review",
    ],
    whatToDo: "Upload management representation letters, tax provision workpapers, related-party schedules, board meeting minutes, attorney letters, and cash flow forecasts. AuditPro AI will assess cross-cutting risks including going concern indicators, related-party completeness, and tax position reasonableness.",
  },
};

// Helper to find guide regardless of key casing
const getCategoryGuide = (key) => {
  if (CATEGORY_GUIDES[key]) return CATEGORY_GUIDES[key];
  const lowerKey = key?.toLowerCase();
  const match = Object.keys(CATEGORY_GUIDES).find(k => k.toLowerCase() === lowerKey);
  return match ? CATEGORY_GUIDES[match] : null;
};

const SUGGESTED_DOCUMENTS = {
  "Cash and cash equivalents": [
    "Bank Statements",
    "Bank Reconciliation",
  ],
  "Investments": [
    "Investment schedules",
    "Brokerage statements",
  ],
  "Fixed Assets": [
    "Fixed asset schedule",
    "Depreciation schedule",
  ],
  "Receivables": [
    "Aged receivables report",
    "Allowance for doubtful accounts",
    "Revenue reconciliation",
  ],
  "Accounts Payable": [
    "AP aging",
  ],
  "Accrued Expenses": [
    "Accrued expense schedule",
  ],
  "Short Term Debt": [
    "LOC statement",
    "LOC agreement",
  ],
  "Long Term Debt": [
    "Notes payable schedule",
    "Loan statements",
    "Loan agreements",
    "Loan covenants test",
  ],
  "Equity": [
    "Equity schedule",
  ],
  "Revenue": [
    "Revenue schedule",
  ],
  "Operating Expenses": [
    "Expense schedule",
  ],
  "Other Income and Expenses": [
    "Other income schedule",
    "Other expenses schedule",
  ],
  "General": [
    "Engagement letter",
    "Budget",
  ],
};

const FileRoom = ({
  engagementId,
  clientId,
  clientName,
  engagementData,
  handlePrevious,
  handleNext,
  onStepComplete,
}) => {
  const navigate = useNavigate();
  const chatEndRef = useRef(null);

  // State
  const [activeCategory, setActiveCategory] = useState("Planning");
  const [expandedCategory, setExpandedCategory] = useState("Planning");
  const [companySummaryFromDb, setCompanySummaryFromDb] = useState("");
  const [isQuerying, setIsQuerying] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [conversations, setConversations] = useState({});
  const [loading, setLoading] = useState(true);

  const [expandedSubCategory, setExpandedSubCategory] = useState(null);
  const [docInfoOpen, setDocInfoOpen] = useState(null); // tracks which subcategory's doc info panel is open
  const [analyzingSubCategory, setAnalyzingSubCategory] = useState(null);
  const [subCategoryAnalysis, setSubCategoryAnalysis] = useState({}); // { "Cash & Cash Equivalents": "## Analysis...", ... }

  // Analysis status per sub-category: { "Cash & Cash Equivalents": "processing", ... }
  const [analysisStatuses, setAnalysisStatuses] = useState({});
  // Track if a DB file was deleted since last AI run (needs re-run)
  const [dbFileDeletedSinceAI, setDbFileDeletedSinceAI] = useState({});
  // Previously uploaded files from DB: { "Cash & Cash Equivalents": [{ fileUrl, _id }, ...], ... }
  const [dbFiles, setDbFiles] = useState({});
  // Polling refs
  const pollingIntervals = useRef({});

  // File removal confirmation modal state
  const [removeFileModal, setRemoveFileModal] = useState({ show: false, group: null, docName: null, fileId: null, isDeleting: false });
  const [removeSubCatModal, setRemoveSubCatModal] = useState({ show: false, category: null, subName: null, isDeleting: false });

  // Custom subcategories: { "Assets": ["Custom Sub 1", ...], "Liabilities": [...] }
  const [customSubCategories, setCustomSubCategories] = useState({});
  // Hidden default subcategories: { "Assets": ["Cash & Cash Equivalents", ...] }
  const [newSubCatInput, setNewSubCatInput] = useState(""); // input value for new subcategory
  const [addingSubCatFor, setAddingSubCatFor] = useState(null); // which category is currently adding
  const [savingSubCat, setSavingSubCat] = useState(false);

  const fetchCustomSubCategories = useCallback(async () => {
    if (!engagementId) return;
    try {
      const authToken = localStorage.getItem("authToken");
      const res = await axios.get(
        `${apiConfig.apiBaseURL}/customSubCategories/${engagementId}`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (res.data.success) {
        setCustomSubCategories(res.data.subCategories || {});
      }
    } catch (err) {
      console.error("Error fetching custom subcategories:", err);
    }
  }, [engagementId]);

  const handleAddSubCategory = async (categoryKey) => {
    const name = newSubCatInput.trim();
    if (!name || savingSubCat) return;

    setSavingSubCat(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const res = await axios.post(
        `${apiConfig.apiBaseURL}/customSubCategories`,
        {
          engagementId,
          category: categoryKey,
          subCategoryName: name,
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        setCustomSubCategories((prev) => ({
          ...prev,
          [categoryKey]: [...(prev[categoryKey] || []), name],
        }));
        setNewSubCatInput("");
        setAddingSubCatFor(null);
        setExpandedSubCategory(name);
        toast.success("Sub-category added");
      }
    } catch (err) {
      console.error("Error creating custom subcategory:", err);
      const msg = err.response?.data?.message || "Failed to add sub-category";
      toast.error(msg);
    } finally {
      setSavingSubCat(false);
    }
  };

  const closeRemoveSubCatModal = () => {
    setRemoveSubCatModal({ show: false, category: null, subName: null, isDeleting: false });
  };

  const handleConfirmRemoveSubCategory = async () => {
    const { category: categoryKey, subName } = removeSubCatModal;
    setRemoveSubCatModal(prev => ({ ...prev, isDeleting: true }));

    try {
      const authToken = localStorage.getItem("authToken");
      const res = await axios.delete(
        `${apiConfig.apiBaseURL}/customSubCategories/${engagementId}`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          data: {
            category: categoryKey,
            subCategoryName: subName,
          },
        }
      );

      if (res.data.success) {
        setCustomSubCategories((prev) => ({
          ...prev,
          [categoryKey]: (prev[categoryKey] || []).filter((s) => s !== subName),
        }));
        // Clean up related state
        if (expandedSubCategory === subName) setExpandedSubCategory(null);
        setUploadedFiles((prev) => { const u = { ...prev }; delete u[subName]; return u; });
        setDbFiles((prev) => { const u = { ...prev }; delete u[subName]; return u; });
        setAnalysisStatuses((prev) => { const u = { ...prev }; delete u[subName]; return u; });
        setSubCategoryAnalysis((prev) => { const u = { ...prev }; delete u[subName]; return u; });
        setDbFileDeletedSinceAI((prev) => { const u = { ...prev }; delete u[subName]; return u; });
        toast.success("Sub-category removed");
      }
    } catch (err) {
      console.error("Error deleting custom subcategory:", err);
      toast.error("Failed to remove sub-category");
    }

    closeRemoveSubCatModal();
  };

  // File upload state: { "Cash & Cash Equivalents": { "Yearend bank statements": File, ... }, ... }
  const [uploadedFiles, setUploadedFiles] = useState({});
  const fileInputRef = useRef(null);
  const [activeUploadKey, setActiveUploadKey] = useState(null); // "group::docName"

  const [dragOverGroup, setDragOverGroup] = useState(null);

  const handleFileUploadClick = (group, docName) => {
    setActiveUploadKey(`${group}::${docName}`);
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !activeUploadKey) return;
    const [group] = activeUploadKey.split("::");
    addFilesToGroup(group, [file]);
    setActiveUploadKey(null);
  };

  const addFilesToGroup = (group, files) => {
    setUploadedFiles((prev) => {
      const groupFiles = { ...(prev[group] || {}) };
      files.forEach((file) => {
        groupFiles[file.name] = file;
      });
      return { ...prev, [group]: groupFiles };
    });
  };

  const handleDragOver = (e, group) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroup(group);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroup(null);
  };

  const handleDrop = (e, group) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroup(null);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFilesToGroup(group, files);
    }
  };

  const handleRemoveFile = (group, docName) => {
    setRemoveFileModal({ show: true, group, docName, fileId: null });
  };

  const handleDeleteDbFile = (group, fileId) => {
    setRemoveFileModal({ show: true, group, docName: null, fileId });
  };

  const closeRemoveFileModal = () => {
    setRemoveFileModal({ show: false, group: null, docName: null, fileId: null, isDeleting: false });
  };

  const handleConfirmRemoveFile = async () => {
    const { group, docName, fileId } = removeFileModal;
    setRemoveFileModal(prev => ({ ...prev, isDeleting: true }));

    if (fileId) {
      try {
        const authToken = localStorage.getItem("authToken");
        const res = await axios.delete(
          `${apiConfig.apiBaseURL}/fileRoomDocuments/${engagementId}/files/${fileId}`,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        if (res.data.success) {
          setDbFiles((prev) => {
            const updated = { ...prev };
            if (updated[group]) {
              updated[group] = updated[group].filter((f) => f._id !== fileId);
              if (updated[group].length === 0) {
                delete updated[group];
              }
            }
            return updated;
          });
          toast.success("File removed successfully");
          setDbFileDeletedSinceAI((prev) => ({ ...prev, [group]: true }));
        }
      } catch (err) {
        console.error("Error deleting file:", err);
        toast.error("Failed to remove file");
      }
    } else {
      setUploadedFiles((prev) => {
        const updated = { ...prev };
        if (updated[group]) {
          const groupFiles = { ...updated[group] };
          delete groupFiles[docName];
          updated[group] = groupFiles;
        }
        return updated;
      });
    }

    // Always close and reset state at the end
    closeRemoveFileModal();
  };

  // Poll analysis status for a specific sub-category
  const startPolling = useCallback((category, subCategory) => {
    const key = `${category}::${subCategory}`;
    // Don't start duplicate polling
    if (pollingIntervals.current[key]) return;

    setAnalysisStatuses((prev) => ({ ...prev, [subCategory]: "processing" }));
    setAnalyzingSubCategory(subCategory);

    const poll = async () => {
      try {
        const authToken = localStorage.getItem("authToken");
        const res = await axios.get(
          `${apiConfig.apiBaseURL}/analysisStatus/${engagementId}?category=${encodeURIComponent(category)}&subCategory=${encodeURIComponent(subCategory)}`,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        const { analysisStatus, analysisError } = res.data;
        setAnalysisStatuses((prev) => ({ ...prev, [subCategory]: analysisStatus }));

        if (analysisStatus === "completed") {
          clearInterval(pollingIntervals.current[key]);
          delete pollingIntervals.current[key];
          setAnalyzingSubCategory(null);
          // Fetch the analysis results
          await fetchFileRoomDocuments();
          toast.success(`Analysis complete for ${subCategory}`);
        } else if (analysisStatus === "failed") {
          clearInterval(pollingIntervals.current[key]);
          delete pollingIntervals.current[key];
          setAnalyzingSubCategory(null);
          const detail = analysisError ? ` ${analysisError}` : " Please try again.";
          toast.error(`Analysis failed for ${subCategory}.${detail}`, { autoClose: 8000 });
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    };

    // Poll immediately, then every 5 seconds
    poll();
    pollingIntervals.current[key] = setInterval(poll, 5000);
  }, [engagementId]);

  // Fetch all file room documents for this engagement
  const fetchFileRoomDocuments = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const res = await axios.get(
        `${apiConfig.apiBaseURL}/fileRoomDocuments/${engagementId}`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (res.data.success && res.data.documents) {
        const newAnalysis = {};
        const newStatuses = {};
        const newDbFiles = {};

        res.data.documents.forEach((doc) => {
          if (doc.aiAnalysis) {
            newAnalysis[doc.subCategory] = doc.aiAnalysis;
          }
          if (doc.analysisStatus) {
            newStatuses[doc.subCategory] = doc.analysisStatus;
          }
          if (doc.files && doc.files.length > 0) {
            newDbFiles[doc.subCategory] = doc.files.filter((f) => !f.deleted);
          }
        });

        setSubCategoryAnalysis((prev) => ({ ...prev, ...newAnalysis }));
        setAnalysisStatuses((prev) => ({ ...prev, ...newStatuses }));
        setDbFiles(newDbFiles);

        // Resume polling for any still-processing items
        res.data.documents.forEach((doc) => {
          if (doc.analysisStatus === "processing") {
            startPolling(doc.category, doc.subCategory);
          }
        });
      }
    } catch (err) {
      console.error("Error fetching file room documents:", err);
    }
  }, [engagementId, startPolling]);

  const handleRunAI = async (e, group) => {
    e.stopPropagation();
    const groupFiles = uploadedFiles[group];
    const hasNewFiles = groupFiles && Object.keys(groupFiles).length > 0;
    const hasDbFileDeleted = dbFileDeletedSinceAI[group];
    if (!hasNewFiles && !hasDbFileDeleted) return;

    setAnalyzingSubCategory(group);
    setAnalysisStatuses((prev) => ({ ...prev, [group]: "processing" }));
    setDbFileDeletedSinceAI((prev) => ({ ...prev, [group]: false }));

    try {
      const authToken = localStorage.getItem("authToken");
      let uploadedFileUrls = [];

      if (hasNewFiles) {
        const filesToUpload = Object.values(groupFiles);

        // Step 1: ask backend for presigned PUT URLs
        const presignRes = await axios.post(
          `${apiConfig.apiBaseURL}/getFileRoomUploadUrls`,
          {
            engagementId,
            category: activeCategory,
            subCategory: group,
            files: filesToUpload.map((file) => ({
              fileName: file.name,
              contentType: file.type || "application/octet-stream",
            })),
          },
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        const uploads = presignRes?.data?.uploads || [];
        if (uploads.length !== filesToUpload.length) {
          console.error("getFileRoomUploadUrls response:", presignRes?.data);
          throw new Error(
            presignRes?.data?.message || "Failed to prepare file uploads"
          );
        }

        // Step 2: upload files directly to S3 with bounded parallelism + retry.
        const uploadSingleFileWithRetry = async (upload, index) => {
          const file = filesToUpload[index];
          const contentType = file.type || "application/octet-stream";

          for (let attempt = 0; attempt <= MAX_S3_UPLOAD_RETRIES; attempt += 1) {
            try {
              // Use a bare axios instance and null out Authorization so our app's
              // global Bearer token doesn't conflict with the S3 presigned signature.
              const s3Client = axios.create();
              await s3Client.put(upload.uploadUrl, file, {
                headers: {
                  "Content-Type": contentType,
                  Authorization: null,
                  "x-api-key": null,
                },
                transformRequest: [(data) => data],
                timeout: 120000,
              });
              return;
            } catch (uploadError) {
              if (attempt === MAX_S3_UPLOAD_RETRIES) {
                throw uploadError;
              }
              const retryDelayMs = 400 * (attempt + 1);
              await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            }
          }
        };

        for (let i = 0; i < uploads.length; i += MAX_PARALLEL_S3_UPLOADS) {
          const batch = uploads.slice(i, i + MAX_PARALLEL_S3_UPLOADS);
          await Promise.all(
            batch.map((upload, batchIndex) => uploadSingleFileWithRetry(upload, i + batchIndex))
          );
        }

        uploadedFileUrls = uploads.map((u) => ({ fileUrl: u.fileUrl }));
      }

      // Step 3: queue analysis with URLs already in S3
      const res = await axios.post(
        `${apiConfig.apiBaseURL}/queueFileRoomAnalysis`,
        {
          engagementId,
          category: activeCategory,
          subCategory: group,
          uploadedFileUrls,
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (res.data.success) {
        // Clear local files since they're now on S3
        if (hasNewFiles) {
          setUploadedFiles((prev) => {
            const updated = { ...prev };
            delete updated[group];
            return updated;
          });
        }
        // Refresh DB files immediately so they show in sidebar while analyzing
        await fetchFileRoomDocuments();
        if (res.data.message === "No files remaining. Analysis cleared.") {
          // All files were deleted — no analysis to poll for
          setAnalyzingSubCategory(null);
          setAnalysisStatuses((prev) => ({ ...prev, [group]: null }));
          setSubCategoryAnalysis((prev) => {
            const updated = { ...prev };
            delete updated[group];
            return updated;
          });
          toast.info("All files removed. Analysis cleared.");
        } else {
          // Start polling for analysis completion
          startPolling(activeCategory, group);
        }
      }
    } catch (err) {
      console.error("AI analysis error:", err);
      setAnalyzingSubCategory(null);
      setAnalysisStatuses((prev) => ({ ...prev, [group]: null }));
      toast.error("Failed to start analysis. Please try again.");
    }
  };

  // Build categories dynamically: Planning + default categories + any extra from customSubCategories
  const FILE_ROOM_CATEGORIES = React.useMemo(() => {
    const dbCats = Object.keys(customSubCategories);

    // Normalize to default labels if name matches case-insensitively
    const normalizedUniqueCats = [...new Set([
      ...DEFAULT_CATEGORIES.map(c => c.key),
      ...dbCats
    ])].reduce((acc, cat) => {
      const existing = acc.find(c => c.toLowerCase() === cat.toLowerCase());
      if (!existing) {
        acc.push(cat);
      }
      return acc;
    }, []);

    const sortedCats = normalizedUniqueCats
      .filter(catKey => catKey.toLowerCase() !== "cost of goods sold")
      .map((catKey) => {
        const defaultMatch = DEFAULT_CATEGORIES.find(dc => dc.key.toLowerCase() === catKey.toLowerCase());
        return {
          key: defaultMatch ? defaultMatch.key : catKey,
          label: defaultMatch ? defaultMatch.label : catKey
        };
      });

    // Move "General" to the end
    const generalIdx = sortedCats.findIndex(c => c.key.toLowerCase() === "general");
    if (generalIdx > -1) {
      const general = sortedCats.splice(generalIdx, 1)[0];
      sortedCats.push(general);
    }

    return sortedCats;
  }, [customSubCategories]);

  // Extract file name from S3 URL or fileUrl object
  const getFileName = (fileData) => {
    if (!fileData) return null;
    let url = null;
    if (typeof fileData === "string") {
      url = fileData;
    } else if (fileData.fileUrl) {
      url = fileData.fileUrl;
    } else if (fileData.url) {
      url = fileData.url;
    }
    if (!url) return null;
    try {
      const decoded = decodeURIComponent(url);
      let name = decoded.split("/").pop().split("?")[0];
      // Strip leading timestamp prefix (e.g. "1775128895538-filename.pdf" → "filename.pdf")
      name = name.replace(/^\d{10,}-/, "");
      return name || null;
    } catch {
      let name = url.split("/").pop().split("?")[0] || null;
      if (name) name = name.replace(/^\d{10,}-/, "");
      return name;
    }
  };

  // Check if a file field has a valid URL (handles object, string, or nested)
  const hasFileUrl = (fileData) => {
    if (!fileData) return false;
    if (typeof fileData === "string" && fileData.length > 0) return true;
    if (typeof fileData === "object") {
      if (fileData.fileUrl && fileData.fileUrl.length > 0) return true;
      if (fileData.url && fileData.url.length > 0) return true;
    }
    return false;
  };

  // Get the 3 planning files from engagement data
  const getPlanningFiles = () => {
    const data = engagementFiles || engagementData;
    const files = [];
    const cyTB = data?.currentYearTrialBalance;
    const pyTB = data?.priorYearTrialBalance;
    const pyFS = data?.priorYearFinancials;

    if (hasFileUrl(cyTB)) {
      files.push({
        label: "Current year trial balance",
        fileName: getFileName(cyTB),
      });
    }
    if (hasFileUrl(pyTB)) {
      files.push({
        label: "Prior Year Trial Balance",
        fileName: getFileName(pyTB),
      });
    }
    if (hasFileUrl(pyFS)) {
      files.push({
        label: "Prior Year Financial Statement",
        fileName: getFileName(pyFS),
      });
    }
    return files;
  };

  // State for engagement files (fetched directly)
  const [engagementFiles, setEngagementFiles] = useState(null);

  // Fetch file room data, file room documents, and custom subcategories on mount
  useEffect(() => {
    if (engagementId) {
      Promise.all([
        fetchFileRoomData(),
        fetchFileRoomDocuments(),
        fetchCustomSubCategories(),
      ]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Cleanup all polling intervals on unmount
    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
      pollingIntervals.current = {};
    };
  }, [engagementId]);

  const fetchFileRoomData = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const res = await axios.get(
        `${apiConfig.apiBaseURL}/getFileRoomData/${engagementId}`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.data.success) {
        setEngagementFiles(res.data.files);
        setCompanySummaryFromDb(res.data.companySummary || "");
      }
    } catch (err) {
      console.error("Error fetching engagement files:", err);
    }
  };

  // Scroll chat to bottom when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversations, activeCategory, expandedSubCategory]);

  const getConversationKey = () => {
    if (activeCategory === "Planning") return "Planning";
    return expandedSubCategory
      ? `${activeCategory}::${expandedSubCategory}`
      : activeCategory;
  };


  // Format currency for display
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "$0";
    const abs = Math.abs(num);
    if (abs >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
    return `$${num.toFixed(2)}`;
  };

  // Build structured planning summary from PAR data matching the screenshot format
  const buildPlanningSummary = () => {
    if (companySummaryFromDb && companySummaryFromDb.trim()) return companySummaryFromDb;

    // This panel must reflect only the stored companysummary output (not RAG / PAR fallbacks).
    return "No company summary available yet. Generate it and refresh.";
  };

  // Get analysis for active category
  const getCategoryAnalysis = (categoryKey) => {
    if (categoryKey === "Planning") {
      return buildPlanningSummary();
    }
    return null;
  };

  const getCategoryIcon = (key, isWhite) => {
    const className = `me-2 ${isWhite ? "text-white" : "text-primary"}`;
    const size = 16;
    switch (key?.toLowerCase()) {
      case "planning": return <FiClipboard size={size} className={className} />;
      case "assets": return <FiDollarSign size={size} className={className} />;
      case "liabilities": return <FiCreditCard size={size} className={className} />;
      case "equity": return <FiUsers size={size} className={className} />;
      case "revenue": return <FiTrendingUp size={size} className={className} />;
      case "operating expenses": return <FiTrendingDown size={size} className={className} />;
      case "other income and expenses": return <FiTrendingUp size={size} className={className} />;
      case "general": return <FiGrid size={size} className={className} />;
      default: return <FiClipboard size={size} className={className} />;
    }
  };

  const getCategoryLabel = (key) => {
    const cat = FILE_ROOM_CATEGORIES.find((c) => c.key === key);
    return cat ? cat.label : key;
  };

  // Count sub-categories per category
  const getCategoryItemCount = (categoryKey) => {
    if (categoryKey === "Planning")
      return companySummaryFromDb && companySummaryFromDb.trim() ? 1 : 0;
    return (customSubCategories[categoryKey] || []).length;
  };

  // Calculate progress based on categories with sub-categories
  const getUploadProgress = () => {
    const categoriesWithData = FILE_ROOM_CATEGORIES.filter((cat) => {
      if (cat.key === "Planning") return companySummaryFromDb && companySummaryFromDb.trim();
      return (customSubCategories[cat.key] || []).length > 0;
    }).length;

    if (categoriesWithData === 0) return 0;

    return Math.round(
      (categoriesWithData / FILE_ROOM_CATEGORIES.length) * 100
    );
  };

  const handleCategoryClick = (categoryKey) => {
    setActiveCategory(categoryKey);
    setExpandedCategory(expandedCategory === categoryKey ? null : categoryKey);
    setExpandedSubCategory(null); // Reset sub-expansion when main changes
  };

  const handleSubCategoryClick = (e, group) => {
    e.stopPropagation();
    const isOpening = expandedSubCategory !== group;
    setExpandedSubCategory(isOpening ? group : null);
    // Auto-open doc info panel when subcategory first expands
    setDocInfoOpen(isOpening ? group : null);
  };

  const handleAskQuestion = async (question) => {
    if (!question?.trim() || !engagementId) return;

    if (activeCategory !== "Planning" && !expandedSubCategory) {
      toast.error("Select a sub-category before asking a question.");
      return;
    }

    const conversationKey = getConversationKey();

    const userMessage = { role: "user", content: question.trim() };
    const currentConvo = conversations[conversationKey] || [];
    const updatedConvo = [...currentConvo, userMessage];

    setConversations((prev) => ({
      ...prev,
      [conversationKey]: updatedConvo,
    }));
    setChatInput("");
    setIsQuerying(true);

    try {
      const authToken = localStorage.getItem("authToken");
      const payload = {
        engagementId,
        question: question.trim(),
        conversationHistory: currentConvo,
      };

      if (activeCategory !== "Planning") {
        payload.category = activeCategory;
        payload.subCategory = expandedSubCategory;
      }

      if (apiConfig.queryFileRoomStreamURL) {
        // Streaming path — Lambda Function URL with RESPONSE_STREAM, SSE-style frames.
        const streamResponse = await fetch(apiConfig.queryFileRoomStreamURL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        if (!streamResponse.ok) {
          const errBody = await streamResponse.json().catch(() => ({}));
          throw new Error(errBody.message || `Request failed with status ${streamResponse.status}`);
        }

        // Seed an empty assistant message we'll fill as tokens arrive.
        let assistantAnswer = "";
        setConversations((prev) => ({
          ...prev,
          [conversationKey]: [...updatedConvo, { role: "assistant", content: "" }],
        }));

        const reader = streamResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let streamError = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() || "";

          for (const frame of frames) {
            if (!frame.startsWith("data: ")) continue;
            let evt;
            try {
              evt = JSON.parse(frame.slice(6));
            } catch (_) {
              continue;
            }
            if (evt.type === "token") {
              assistantAnswer += evt.text;
              const latestAnswer = assistantAnswer;
              setConversations((prev) => ({
                ...prev,
                [conversationKey]: [
                  ...updatedConvo,
                  { role: "assistant", content: latestAnswer },
                ],
              }));
            } else if (evt.type === "error") {
              streamError = evt.message || "Streaming error";
            }
            // "done" frame: no action needed, loop will exit when reader closes.
          }
        }

        if (streamError) {
          throw new Error(streamError);
        }
      } else {
        // Buffered path — API Gateway REST, full JSON response.
        const response = await axios.post(
          `${apiConfig.apiBaseURL}/queryFileRoom`,
          payload,
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.success) {
          const assistantMessage = {
            role: "assistant",
            content: response.data.answer,
            sources: response.data.sources || [],
          };
          setConversations((prev) => ({
            ...prev,
            [conversationKey]: [...updatedConvo, assistantMessage],
          }));
        }
      }
    } catch (err) {
      console.error("Query error:", err);
      // Detailed error for debugging
      const errorDetail = err.response?.data?.message || err.message || "Unknown error";
      const errorMessage = {
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorDetail}`,
      };
      setConversations((prev) => ({
        ...prev,
        [conversationKey]: [...updatedConvo, errorMessage],
      }));
    } finally {
      setIsQuerying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAskQuestion(chatInput);
    }
  };

  const [pdfDownloading, setPdfDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    if (pdfDownloading) return;
    setPdfDownloading(true);

    try {
      const authToken = localStorage.getItem("authToken");
      const payload = {
        engagementId,
        activeCategory,
        format: "word",
      };
      if (expandedSubCategory) {
        payload.subCategory = expandedSubCategory;
      }

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/downloadFileRoomReport`,
        payload,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
        }
      );
      const { file, fileName: serverFileName, mimeType, success } = response.data;
      if (!success || !file) {
        throw new Error(response.data?.message || "Server did not return a valid file.");
      }

      const byteCharacters = atob(file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type:
          mimeType ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const fileName = serverFileName;

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      toast.success("Report downloaded successfully!");
    } catch (error) {
      console.error("Report download error:", error);
      const serverMessage = error?.response?.data?.message;
      toast.error(serverMessage || "Failed to download report. Please try again.");
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleSaveAndExit = () => {
    navigate("/engagements");
  };

  const handleContinueToFAR = () => {
    if (onStepComplete) onStepComplete();
    handleNext();
  };

  if (loading) {
    return <Loader text="Loading File Room..." />;
  }

  const currentConversation = conversations[getConversationKey()] || [];
  const currentAnalysis = getCategoryAnalysis(activeCategory);
  const suggestedQuestions = SUGGESTED_QUESTIONS[activeCategory] || [
    "What are the key risks?",
    "Any significant changes?",
    "Items requiring attention?",
    "Compliance considerations?",
  ];
  const uploadProgress = getUploadProgress();
  const client =
    clientName || engagementData?.clientDetails?.clientName || "";
  const engYear = engagementData?.engagementYear || "";
  const currentItemCount = getCategoryItemCount(activeCategory);

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.xlsx,.xls,.docx"
        onChange={handleFileChange}
      />
      <Container fluid className="file-room-container pt-1 pb-3">
        <Row>
          {/* Left Sidebar */}
          <Col lg={4} xl={3}>
            <Card className="file-room-sidebar border-0 shadow-sm">
              <Card.Body className="p-3">
                {/* Categories */}
                <div className="file-room-categories">
                  {FILE_ROOM_CATEGORIES.map((category) => {
                    const isActive = activeCategory === category.key;
                    const isExpanded = expandedCategory === category.key;
                    const hasAnalysis = !!getCategoryAnalysis(category.key);
                    const planningFiles =
                      category.key === "Planning"
                        ? getPlanningFiles()
                        : [];

                    return (
                      <div key={category.key} className={`mb-2 category-wrapper ${isExpanded ? "expanded" : ""}`}>
                        {/* Category header */}
                        <div
                          className={`category-item d-flex align-items-center justify-content-between p-3 cursor-pointer ${isActive ? "active" : ""
                            } ${isExpanded ? "expanded-header" : "rounded"}`}
                          onClick={() => handleCategoryClick(category.key)}
                        >
                          <div className="d-flex align-items-center">
                            <span className={`fw-semibold ${isActive || isExpanded ? "text-white" : "text-dark"}`}>
                              {category.label}
                            </span>
                            {hasAnalysis && (
                              <span className="complete-badge ms-2">Complete</span>
                            )}
                          </div>
                          <div className="d-flex align-items-center">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 12 12"
                              fill="none"
                              className={`category-chevron ${isExpanded ? "rotated" : ""
                                }`}
                            >
                              <path
                                d="M3 4.5L6 7.5L9 4.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div className="category-expanded-content px-3 pb-3">
                            {/* Planning: show the 3 uploaded engagement files */}
                            {category.key === "Planning" &&
                              planningFiles.length > 0 &&
                              planningFiles.map((file, idx) => (
                                <div
                                  key={idx}
                                  className="file-card"
                                >
                                  <p className="file-card-label mb-1">
                                    {file.label}
                                  </p>
                                  <p className="file-card-meta mb-0">
                                    {file.fileName}{file.fileSize ? ` · ${file.fileSize}` : ""}
                                  </p>
                                </div>
                              ))}

                            {/* All sub-categories from DB (single source of truth) */}
                            {(customSubCategories[category.key] || []).map((group, idx) => {
                              const isSubExpanded = expandedSubCategory === group;
                              const docs = SUGGESTED_DOCUMENTS[group] || [];
                              return (
                                <div key={idx} className={`subcategory-wrapper ${isSubExpanded ? "expanded" : ""}`}>
                                  <div
                                    className={`subcategory-header d-flex align-items-center justify-content-between p-3 cursor-pointer ${isSubExpanded ? "active" : ""}`}
                                    onClick={(e) => handleSubCategoryClick(e, group)}
                                  >
                                    <span className={`fw-semibold ${isSubExpanded ? "text-white" : "text-dark"}`}>
                                      {group}
                                    </span>
                                    <div className="d-flex align-items-center">
                                      {isSubExpanded && (
                                        <button
                                          className="run-ai-btn me-2"
                                          disabled={analysisStatuses[group] === "processing" || analyzingSubCategory === group || ((!uploadedFiles[group] || Object.keys(uploadedFiles[group]).length === 0) && !dbFileDeletedSinceAI[group])}
                                          onClick={(e) => handleRunAI(e, group)}
                                        >
                                          {analysisStatuses[group] === "processing"
                                            ? "Analyzing..."
                                            : analysisStatuses[group] === "completed"
                                              ? "Re-run AI"
                                              : "Run AI"}
                                        </button>
                                      )}
                                      <FiTrash2
                                        size={14}
                                        className={`me-2 cursor-pointer ${isSubExpanded ? "text-white" : "text-muted"}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRemoveSubCatModal({ show: true, category: category.key, subName: group, isDeleting: false });
                                        }}
                                      />
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        className={`category-chevron cursor-pointer ${isSubExpanded ? "rotated" : ""}`}
                                        onClick={(e) => handleSubCategoryClick(e, group)}
                                      >
                                        <path
                                          d="M3 4.5L6 7.5L9 4.5"
                                          stroke={isSubExpanded ? "#fff" : "#677289"}
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </div>
                                  </div>

                                  {isSubExpanded && (
                                    <div className="subcategory-body px-3 pb-3">
                                      {/* Documents info dropdown (only if suggested docs exist) */}
                                      {docs.length > 0 && (
                                        <div className={`doc-info-panel mt-2 ${docInfoOpen === group ? "open" : ""}`}>
                                          <div
                                            className="doc-info-toggle d-flex align-items-center justify-content-between px-3 py-2 cursor-pointer"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setDocInfoOpen(docInfoOpen === group ? null : group);
                                            }}
                                          >
                                            <div className="d-flex align-items-center">
                                              <BsFileEarmarkCheck className="doc-info-icon me-2" size={15} />
                                              <span className="doc-info-title">
                                                Documents considered for upload
                                              </span>
                                            </div>
                                            <svg
                                              width="14"
                                              height="14"
                                              viewBox="0 0 12 12"
                                              fill="none"
                                              className={`category-chevron ${docInfoOpen === group ? "rotated" : ""}`}
                                            >
                                              <path
                                                d="M3 4.5L6 7.5L9 4.5"
                                                stroke="#677289"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </div>
                                          {docInfoOpen === group && (
                                            <div className="doc-info-list">
                                              {docs.map((doc, dIdx) => (
                                                <div key={dIdx} className="doc-info-item d-flex align-items-center">
                                                  <BsFileEarmarkText className="doc-info-item-icon me-2 flex-shrink-0" size={14} />
                                                  <span>{doc}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Upload dropzone */}
                                      <div
                                        className={`doc-upload-zone mt-3 text-center cursor-pointer ${dragOverGroup === group ? "drag-active" : ""}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFileUploadClick(group, "__general__");
                                        }}
                                        onDragOver={(e) => handleDragOver(e, group)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={(e) => handleDrop(e, group)}
                                      >
                                        <div className="doc-upload-icon-wrapper">
                                          <LuUploadCloud size={18} />
                                        </div>
                                        <p className="doc-upload-title mb-0">
                                          {dragOverGroup === group ? "Drop files here" : "Click or drag files to upload"}
                                        </p>
                                        <p className="doc-upload-hint mb-0">
                                          PDF, Excel, Word, or Image files
                                        </p>
                                      </div>

                                      {/* Uploaded files (local, pending upload) */}
                                      {uploadedFiles[group] && Object.keys(uploadedFiles[group]).length > 0 && (
                                        <div className="uploaded-files-list mt-2">
                                          <div className="uploaded-files-header d-flex align-items-center mb-1 px-1">
                                            <BsFileEarmarkPlus className="text-muted me-1" size={12} />
                                            <span className="uploaded-files-label">
                                              Ready to upload ({Object.keys(uploadedFiles[group]).length})
                                            </span>
                                          </div>
                                          {Object.entries(uploadedFiles[group]).map(([docName, file]) => (
                                            <div key={docName} className="uploaded-file-item d-flex align-items-start justify-content-between">
                                              <div className="d-flex align-items-start" style={{ minWidth: 0 }}>
                                                <BsFileEarmarkCheck className="uploaded-file-icon me-2 flex-shrink-0" size={15} />
                                                <span className="uploaded-file-name">
                                                  {file.name}
                                                </span>
                                              </div>
                                              <FiX
                                                className="uploaded-file-remove flex-shrink-0 ms-2 cursor-pointer"
                                                size={15}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleRemoveFile(group, docName);
                                                }}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Previously uploaded files from DB */}
                                      {dbFiles[group] && dbFiles[group].length > 0 && (
                                        <div className="uploaded-files-list mt-2">
                                          {dbFiles[group].map((file) => (
                                            <div key={file._id} className="uploaded-file-item d-flex align-items-start justify-content-between">
                                              <div className="d-flex align-items-start" style={{ minWidth: 0 }}>
                                                <BsFileEarmarkCheck className="uploaded-file-icon me-2 flex-shrink-0 text-success" size={15} />
                                                <span className="uploaded-file-name">
                                                  {getFileName(file)}
                                                </span>
                                              </div>
                                              <FiX
                                                className="uploaded-file-remove flex-shrink-0 ms-2 cursor-pointer"
                                                size={15}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteDbFile(group, file._id);
                                                }}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                    </div>
                                  )}
                                </div>
                              );
                            }
                            )}

                            {/* Add Sub - Category */}
                            {(
                              <>
                                <div
                                  className="add-subcat-link mt-2 mb-1 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAddingSubCatFor(addingSubCatFor === category.key ? null : category.key);
                                    setNewSubCatInput("");
                                  }}
                                >
                                  Add Sub - Category
                                </div>

                                {addingSubCatFor === category.key && (
                                  <div className="add-subcat-bar mt-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <Form.Control
                                        type="text"
                                        placeholder="Enter sub-category name"
                                        value={newSubCatInput}
                                        onChange={(e) => setNewSubCatInput(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") handleAddSubCategory(category.key);
                                          if (e.key === "Escape" && !savingSubCat) { setAddingSubCatFor(null); setNewSubCatInput(""); }
                                        }}
                                        autoFocus
                                        disabled={savingSubCat}
                                        className="add-subcat-field"
                                      />
                                      <Button
                                        variant="primary"
                                        className="add-subcat-save-btn"
                                        onClick={() => handleAddSubCategory(category.key)}
                                        disabled={!newSubCatInput.trim() || savingSubCat}
                                      >
                                        {savingSubCat ? (
                                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        ) : "Save"}
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        className="add-subcat-cancel-btn"
                                        onClick={() => { setAddingSubCatFor(null); setNewSubCatInput(""); }}
                                        disabled={savingSubCat}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Content - AI Assistant */}
          <Col lg={8} xl={9}>
            <Card className="file-room-content border-0 shadow-sm d-flex flex-column">
              {/* AI Assistant Header (Fixed at top) */}
              <div className="file-room-chat-header p-4 pb-3 border-bottom">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-center">
                    <img
                      src={logoIcon}
                      alt="AI"
                      className="ai-assistant-icon me-3"
                      style={{ width: 40, height: 40 }}
                    />
                    <div>
                      <h5 className="mb-0 fw-bold">
                        AI Assistant
                      </h5>
                      <p className="text-muted mb-0 small">
                        {client}{engYear ? ` · FY${engYear}` : ""} Engagement
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="link"
                    className="text-dark p-0"
                    title="Download Analysis as Word"
                    onClick={handleDownloadPDF}
                    disabled={pdfDownloading}
                  >
                    {pdfDownloading ? (
                      <span className="spinner-border spinner-border-sm" role="status" />
                    ) : (
                      <LuDownload size={20} />
                    )}
                  </Button>
                </div>
              </div>

              {/* Scrollable body: Analysis + Chat stacked */}
              <div className="file-room-scroll-area flex-grow-1">
                <div className="p-4 pt-4">
                  <div className="ai-analysis-card p-4 rounded shadow-sm mb-4">
                    <h6 className="fw-bold text-dark mb-3">
                      AI Analysis &mdash; {
                        (expandedSubCategory && subCategoryAnalysis[expandedSubCategory])
                          ? expandedSubCategory
                          : currentAnalysis
                            ? getCategoryLabel(activeCategory)
                            : "Planning"
                      }
                    </h6>

                    <div className="d-flex align-items-center mb-3">
                      <BsFileEarmarkText className="text-muted me-2" />
                      <span className="text-muted small">
                        Based on the uploaded {
                          (expandedSubCategory && subCategoryAnalysis[expandedSubCategory])
                            ? expandedSubCategory
                            : currentAnalysis
                              ? getCategoryLabel(activeCategory)
                              : "Planning"
                        } documents
                      </span>
                      {currentItemCount > 0 && (
                        <span className="badge bg-primary ms-2 rounded-pill">
                          +{currentItemCount}
                        </span>
                      )}
                    </div>

                    {/* Report Content */}
                    {(() => {
                      let displayAnalysis =
                        (expandedSubCategory && subCategoryAnalysis[expandedSubCategory])
                          ? subCategoryAnalysis[expandedSubCategory]
                          : currentAnalysis
                            ? currentAnalysis
                            : buildPlanningSummary();

                      if (displayAnalysis && typeof displayAnalysis === "string") {
                        displayAnalysis = displayAnalysis
                          .split("\n")
                          .filter((line) => {
                            const clean = line.replace(/[*#_`]/g, "").trim();
                            if (!clean) return true;
                            if (/^AUDIT\s+(ANALYSIS|OBSERVATIONS|REVIEW|REPORT)/i.test(clean)) return false;
                            if (/\b(LLC|Inc|Corp|LLP|Ltd|Company|Association)\b/i.test(clean) && clean.length < 120) return false;
                            return true;
                          })
                          .join("\n")
                          .replace(/^\n+/, "")
                          .replace(/^---+\n*/m, "");
                      }

                      return displayAnalysis ? (
                        <div className="analysis-content markdown-container">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: (props) => <h2 className="md-h1" {...props} />,
                              h2: (props) => <h3 className="md-h2" {...props} />,
                              h3: (props) => <h4 className="md-h3" {...props} />,
                              h4: (props) => <h5 className="md-h4" {...props} />,
                              strong: (props) => <strong style={{ color: "#1a2332" }} {...props} />,
                              table: (props) => (
                                <div className="table-responsive my-3">
                                  <table className="markdown-table" {...props} />
                                </div>
                              ),
                            }}
                          >
                            {displayAnalysis}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <p className="text-muted">No analysis available yet.</p>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Chat conversation below analysis */}
                  {currentConversation.length > 0 && (
                    <div className="chat-messages mt-2">
                      {currentConversation.map((msg, idx) => (
                        <div key={idx} className={`chat-message mb-4 ${msg.role === "assistant" ? "assistant" : "user"}`}>
                          {msg.role === "assistant" ? (
                            <div className="d-flex">
                              <img src={logoIcon} alt="AI" className="me-2 mt-1" style={{ width: 24, height: 24 }} />
                              <div className="assistant-bubble p-3 rounded shadow-sm">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    h1: (props) => <p className="chat-md-h1" {...props} />,
                                    h2: (props) => <p className="chat-md-h2" {...props} />,
                                    h3: (props) => <p className="chat-md-h3" {...props} />,
                                    h4: (props) => <p className="chat-md-h4" {...props} />,
                                    h5: (props) => <p className="chat-md-h5" {...props} />,
                                    h6: (props) => <p className="chat-md-h6" {...props} />,
                                    table: (props) => (
                                      <div className="table-responsive my-2">
                                        <table className="markdown-table" {...props} />
                                      </div>
                                    ),
                                    thead: (props) => <thead className="markdown-table-head" {...props} />,
                                    th: (props) => <th className="markdown-table-th" {...props} />,
                                    td: (props) => <td className="markdown-table-td" {...props} />,
                                  }}
                                >
                                  {msg.content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-end">
                              <div className="user-bubble p-3 rounded shadow-sm">
                                {msg.content}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {isQuerying && (
                        <div className="d-flex mb-3">
                          <img src={logoIcon} alt="AI" className="me-2 mt-1" style={{ width: 24, height: 24 }} />
                          <div className="assistant-bubble p-3 rounded shadow-sm">
                            <div className="typing-indicator"><span></span><span></span><span></span></div>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  )}
                </div>
              </div>{/* end scroll area */}

              {/* Fixed bottom: suggested questions + input + disclaimer */}
              <div className="file-room-chat-bottom px-4 pb-3 pt-2">
                <div className="suggested-questions d-flex flex-wrap gap-2 mb-3">
                  {suggestedQuestions.map((q, idx) => (
                    <Button
                      key={idx}
                      variant="outline-secondary"
                      size="sm"
                      className="suggested-question-btn rounded-pill"
                      onClick={() => handleAskQuestion(q)}
                      disabled={isQuerying}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
                <div className="chat-input-wrapper d-flex align-items-center">
                  <Form.Control
                    type="text"
                    placeholder={`Ask about ${getCategoryLabel(activeCategory)}...`}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="chat-input border-0"
                    disabled={isQuerying}
                  />
                  <Button
                    variant="primary"
                    className="chat-send-btn rounded-circle ms-2"
                    onClick={() => handleAskQuestion(chatInput)}
                    disabled={!chatInput.trim() || isQuerying}
                  >
                    <IoSend size={16} />
                  </Button>
                </div>
                <p className="text-muted text-center small mt-2 mb-0">
                  AI-generated content may not always be accurate. Always apply professional judgment before relying on this information.
                </p>
              </div>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Footer Navigation */}
      <Container fluid className="px-0 setup-footer">
        <Card
          className="h-100 py-4 px-3 mt-3"
          style={{ border: "2px solid #ECEEF5" }}
        >
          <div className="d-flex justify-content-between mt-3 all-buttons">
            <div className="left">
              <Button
                type="button"
                variant="outline-primary"
                className="px-5 py-3 me-3"
                onClick={handleSaveAndExit}
              >
                Save & Exit
              </Button>
              <Button
                type="button"
                variant="outline-primary"
                className="px-5 py-3"
                onClick={() => handlePrevious(2)}
              >
                Back to PAR
              </Button>
            </div>
            <div className="right">
              <Button
                type="button"
                variant="primary"
                className="px-5 py-3 text-white"
                onClick={handleContinueToFAR}
              >
                Continue to FAR
              </Button>
            </div>
          </div>
        </Card>
      </Container>

      {/* Sub-category removal confirmation modal */}
      <Modal
        show={removeSubCatModal.show}
        onHide={closeRemoveSubCatModal}
        centered
        className="remove-file-modal"
        dialogClassName="remove-file-modal-dialog"
        backdrop="static"
      >
        <Modal.Body className="text-center p-0">
          <div className="remove-file-modal-header-band" />
          <div className="px-4 pt-4 pb-3">
            <div className="remove-file-modal-icon mx-auto mb-3">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h5 className="remove-file-modal-title mb-2">Remove Sub-Category</h5>
            <p className="remove-file-modal-desc mb-4">
              Are you sure you want to remove <strong>{removeSubCatModal.subName}</strong>?
              <br />All uploaded files and AI analysis in this sub-category will be removed.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0 pt-0 pb-4 gap-3 px-4">
          <Button
            className="remove-file-modal-cancel"
            onClick={closeRemoveSubCatModal}
            disabled={removeSubCatModal.isDeleting}
          >
            Cancel
          </Button>
          <Button
            className="remove-file-modal-confirm"
            onClick={handleConfirmRemoveSubCategory}
            disabled={removeSubCatModal.isDeleting}
          >
            {removeSubCatModal.isDeleting ? (
              <span className="d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Removing...
              </span>
            ) : (
              "Remove"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File removal confirmation modal */}
      <Modal
        show={removeFileModal.show}
        onHide={closeRemoveFileModal}
        centered
        className="remove-file-modal"
        dialogClassName="remove-file-modal-dialog"
        backdrop="static"
      >
        <Modal.Body className="text-center p-0">
          <div className="remove-file-modal-header-band" />
          <div className="px-4 pt-4 pb-3">
            <div className="remove-file-modal-icon mx-auto mb-3">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h5 className="remove-file-modal-title mb-2">Remove File</h5>
            <p className="remove-file-modal-desc mb-4">
              Are you sure you want to remove this file?
              {removeFileModal.fileId && (<><br />This action cannot be undone.</>)}
            </p>
            <div className="remove-file-modal-note">
              <div className="remove-file-modal-note-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.75a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zM8 12a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                </svg>
              </div>
              <span>After removing, click <strong>Re-run AI</strong> to refresh the analysis.</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0 pt-0 pb-4 gap-3 px-4">
          <Button
            className="remove-file-modal-cancel"
            onClick={closeRemoveFileModal}
            disabled={removeFileModal.isDeleting}
          >
            Cancel
          </Button>
          <Button
            className="remove-file-modal-confirm"
            onClick={handleConfirmRemoveFile}
            disabled={removeFileModal.isDeleting}
          >
            {removeFileModal.isDeleting ? (
              <span className="d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Removing...
              </span>
            ) : (
              "Remove"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FileRoom;
